import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { Button } from '@components/Button';
import { Icons } from '@components/Icons/Icons';
import dynamic from 'next/dynamic';
import { type ReactElement, type ReactNode, useState } from 'react';
import { IconContext } from 'react-icons';
import { CopyButton } from './CopyButton';

const Modal = dynamic(() => import('../Modal/Modal').then((mod) => mod.Modal));

function SocialIcon({ children }: { children: ReactNode }) {
  return (
    <span className="grid h-12 w-12 place-items-center bg-transparent text-black transition-all delay-0 duration-200 ease-in-out hover:bg-black hover:text-white dark:text-white dark:hover:text-grey-mid">
      {children}
    </span>
  );
}

export function SocialShares({
  type,
  shareSlug,
  titleText,
  imageSource = '', // leave empty string if there isn't a default image for a page
  className,
}: {
  type: string;
  shareSlug: string;
  titleText: string;
  imageSource?: string;
  className?: string;
}): ReactElement {
  const [showModal, setShowModal] = useState(false);

  const fullUrl = `https://www.bulknutrients.com.au${shareSlug}`;
  const titleEncoded = encodeURIComponent(titleText);

  const handleShare = async () => {
    try {
      await navigator.share({
        url: fullUrl,
        title: titleText,
      });
    } catch (err) {
      // User cancelled the native share sheet — do nothing
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      // else desktop
      setShowModal(true);
    }
  };

  return (
    <>
      <span
        className={mergeStyles(
          className ?? '',
          'text-base font-bold text-grey-darkest dark:text-white'
        )}
      >
        <Button onClick={handleShare} buttonStyle="text">
          Share this {type}
        </Button>
      </span>
      {showModal && (
        <Modal
          show={showModal}
          setShow={setShowModal}
          className="dark:bg-black max-w-[400px]"
        >
          <IconContext.Provider value={{ size: '36px' }}>
            <h2 className="mb-8 mt-4 font-bold italic">Share this {type}</h2>
            <div className="w-full">
              <div className="mb-8 gap-2 flex flex-col">
                <span className="font-bold">
                  Click to copy this {type}'s link
                </span>
                <CopyButton copyText={fullUrl} />
              </div>

              <div className="mb-8 flex flex-col">
                <span className="font-bold">
                  Click to email this {type} to a friend
                </span>
                <a
                  href={`mailto:?subject=${titleEncoded}&body=Check%20out%20Bulk%20Nutrients:%20${titleEncoded}! ${fullUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ga-social-share"
                  data-social-id="email"
                  aria-label="Share by Email"
                >
                  <SocialIcon>
                    <Icons.Social.Email />
                  </SocialIcon>
                </a>
              </div>

              <div className="flex flex-col">
                <span className="font-bold">
                  Click to share this {type} to social media
                </span>
                <div className="flex flex-wrap gap-6 justify-between">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${fullUrl}&amp;utm_source=share&amp;utm_medium=facebook`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ga-social-share"
                    data-social-id="facebook"
                    aria-label="Share on Facebook"
                  >
                    <SocialIcon>
                      <Icons.Social.Facebook.F />
                    </SocialIcon>
                  </a>
                  <a
                    href={`fb-messenger://share?link=${encodeURIComponent(
                      fullUrl
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mobile-only ga-social-share block sm:hidden"
                    data-social-id="messenger"
                    aria-label="Share on Messenger"
                  >
                    <SocialIcon>
                      <Icons.Social.Facebook.Messenger />
                    </SocialIcon>
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${fullUrl}&amp;utm_source=share&amp;utm_medium=twitter&text=${titleEncoded}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ga-social-share"
                    data-social-id="twitter"
                    aria-label="Share on Twitter"
                  >
                    <SocialIcon>
                      <Icons.Social.XTwitter />
                    </SocialIcon>
                  </a>
                  <a
                    href={`https://www.pinterest.com.au/pin-builder/?url=${fullUrl}&media=${imageSource}&description=${titleEncoded}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ga-social-share"
                    data-social-id="pinterest"
                    aria-label="Share on Pinterest"
                  >
                    <SocialIcon>
                      <Icons.Social.Pinterest />
                    </SocialIcon>
                  </a>
                  <a
                    href={`https://www.reddit.com/submit?url=${fullUrl}&amp;utm_source=share&amp;utm_medium=reddit&title=${titleEncoded}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ga-social-share"
                    data-social-id="reddit"
                    aria-label="Share on Reddit"
                  >
                    <SocialIcon>
                      <Icons.Social.Reddit />
                    </SocialIcon>
                  </a>
                </div>
              </div>
            </div>
          </IconContext.Provider>
        </Modal>
      )}
    </>
  );
}
